/**
 * Search: iterative-deepening negamax with alpha-beta, quiescence, a verified
 * transposition table, MVV-LVA + killer + history move ordering, and
 * repetition detection that scores a perpetual check against the checker, as
 * the game does (see repetitionScore). Terminal handling encodes the xiangqi rule
 * that having no legal move (checkmate OR stalemate) is a loss.
 *
 * Hot-path performance: king squares are cached on the searcher and updated on
 * king moves, and move legality (own king not attacked, kings not facing) is
 * folded into the search loop so each move is made/unmade exactly once.
 */

import {
  Color,
  PieceType,
  fileOf,
  isSquareAttacked,
  pseudoLegalMoves,
  rankOf,
  type Move,
  type Position,
} from "@xq/rules";
import { PIECE_VALUE, evaluate } from "./eval.js";
import { fullHash, updateHash, type Hash } from "./zobrist.js";

const MATE = 1_000_000;
const INF = 1 << 30;

/**
 * Hard ceiling on how deep the search may recurse, counted in plies from the
 * root and enforced in quiescence.
 *
 * Quiescence has no depth counter of its own: it follows captures, and when the
 * side to move is in check it follows EVERY move so an evasion is never missed.
 * That second rule is the dangerous one, because "am I in check?" is answered
 * from the cached king squares — and along a line where those have gone stale,
 * every node can look like a check, at which point quiescence expands the full
 * move list forever. There was nothing to stop it: no depth to run down, and no
 * clock check either. The result was an uncaught RangeError out of worker.js on
 * /game/computer, which the error log recorded 13 times.
 *
 * 64 is far past anything the deepest level reaches for real (master searches to
 * 24, and quiescence beyond that is bounded by the material left to capture), so
 * this costs no playing strength. It exists to make the recursion finite.
 */
const MAX_PLY = 64;

export type Level = "beginner" | "easy" | "medium" | "hard" | "master";

interface LevelParams {
  maxDepth: number;
  moveTimeMs: number;
  randomness: number;
  blunder: number;
}

export const LEVELS: Record<Level, LevelParams> = {
  /*
   * The first opponent on /play/computer, below `easy`. A player wrote in
   * (2026-10-19) that the AI could not be beaten at all, and `easy` cannot be
   * the answer: three rated resident bots on the game server play at `easy`
   * (services/realtime/src/bots.ts), so weakening it would move their ratings.
   *
   * One ply plus quiescence, so it still answers a capture it can see and still
   * takes a free piece most of the time -- its moves look like moves. It does
   * not see the reply to a quiet move at all, so it walks into threats and never
   * notices a mate coming; a 3-pawn window lets it pick a move that drops a
   * pawn, an advisor or an elephant (anything worth less than a horse) as
   * readily as the best one; and three moves in ten it plays any legal move,
   * which is where it leaves a horse or a chariot hanging and misses a mate in
   * one.
   *
   * Measured when it was added (20-game matches, alternating colours): it lost
   * 20/20 to `easy`; it beat a uniformly random mover 19-1-0, so it is not
   * aimless; 13.5% of its moves left a horse, cannon or chariot free to take
   * (`easy`: 8.7%); and it missed 22% of the mates in one it was offered
   * (`easy`: none).
   */
  beginner: { maxDepth: 1, moveTimeMs: 150, randomness: 300, blunder: 0.3 },
  easy: { maxDepth: 2, moveTimeMs: 250, randomness: 70, blunder: 0.18 },
  medium: { maxDepth: 5, moveTimeMs: 800, randomness: 30, blunder: 0.04 },
  hard: { maxDepth: 9, moveTimeMs: 2000, randomness: 8, blunder: 0 },
  master: { maxDepth: 24, moveTimeMs: 4000, randomness: 0, blunder: 0 },
};

export interface SearchResult {
  move: Move | null;
  score: number;
  depth: number;
  nodes: number;
  mate: number | null;
}

interface TTEntry {
  h2: number;
  depth: number;
  score: number;
  flag: number; // 0 EXACT, 1 LOWER, 2 UPPER
  move: number;
}

const mkey = (m: Move) => m.from * 90 + m.to;

class Searcher {
  private tt = new Map<number, TTEntry>();
  private killers: number[][] = [];
  private history = new Int32Array(90 * 90);
  private nodes = 0;
  private deadline = 0;
  private stopped = false;
  private pathH1: number[] = [];
  /** Parallel to pathH1: was the side to move in check there, i.e. did the move into it give check? */
  private pathChecked: boolean[] = [];
  /** Bumped whenever a repetition is scored as a perpetual check, so the TT can keep out of it. */
  private perpetuals = 0;
  private kingSq: [number, number] = [-1, -1];

  private timeUp(): boolean {
    if ((this.nodes & 2047) === 0 && Date.now() >= this.deadline) this.stopped = true;
    return this.stopped;
  }

  /** Kings facing on an open file, using cached king squares. */
  private facing(pos: Position): boolean {
    const rk = this.kingSq[Color.Red];
    const bk = this.kingSq[Color.Black];
    if (fileOf(rk) !== fileOf(bk)) return false;
    const f = fileOf(rk);
    const lo = Math.min(rankOf(rk), rankOf(bk));
    const hi = Math.max(rankOf(rk), rankOf(bk));
    for (let r = lo + 1; r < hi; r++) if (pos.board[r * 9 + f]) return false;
    return true;
  }

  /** After a move by `mover` is on the board, is that move legal? */
  private legal(pos: Position, mover: Color): boolean {
    if (this.facing(pos)) return false;
    return !isSquareAttacked(pos, this.kingSq[mover], mover === Color.Red ? Color.Black : Color.Red);
  }

  /**
   * Score a repeated position the way the game adjudicates it (WXF, as in
   * @xq/rules adjudicatePerpetualCheck): a side that gave check on every one of
   * its moves round the cycle loses, unless the other side did too.
   *
   * This used to return 0 for every repetition. The game, meanwhile, scores a
   * perpetual check as a loss for the checker -- so a lost engine saw endless
   * checking as a draw, went for it, and made the player dodge the same checks
   * until the game finally ruled on them. A player reported exactly that.
   *
   * The cycle is the positions after the first occurrence, through this one.
   * Each records whether the move into it gave check, and walking back from
   * here those moves alternate: the opponent's at even distances, ours at odd.
   * Chases are left as draws; that half of the law needs more than a search can
   * afford to work out per node.
   */
  private repetitionScore(pos: Position, prev: number, ply: number): number {
    const mover = pos.sideToMove;
    const here = this.pathH1.length;
    let theirMoves = 0, theirChecks = 0, ourMoves = 0, ourChecks = 0;
    for (let k = prev + 1; k <= here; k++) {
      const checked = k === here
        ? isSquareAttacked(pos, this.kingSq[mover], mover === Color.Red ? Color.Black : Color.Red)
        : this.pathChecked[k]!;
      if ((here - k) % 2 === 0) {
        theirMoves++;
        if (checked) theirChecks++;
      } else {
        ourMoves++;
        if (checked) ourChecks++;
      }
    }
    const theyPerpetual = theirMoves > 0 && theirChecks === theirMoves;
    const wePerpetual = ourMoves > 0 && ourChecks === ourMoves;
    if (theyPerpetual === wePerpetual) return 0;
    this.perpetuals++;
    // As decisive as a mate here, so a lost side prefers any longer resistance
    // to forcing the repetition.
    return theyPerpetual ? MATE - ply : -(MATE - ply);
  }

  private captureScore(pos: Position, m: Move): number {
    const victim = pos.board[m.to];
    if (!victim) return 0;
    const attacker = pos.board[m.from]!;
    return 1_000_000 + PIECE_VALUE[victim.type] * 16 - PIECE_VALUE[attacker.type];
  }

  private orderMoves(pos: Position, moves: Move[], ttMove: number, ply: number): Move[] {
    const kill = this.killers[ply] ?? [];
    const scored = moves.map((m) => {
      let s: number;
      const k = mkey(m);
      if (k === ttMove) s = 2_000_000;
      else if (pos.board[m.to]) s = this.captureScore(pos, m);
      else if (k === kill[0] || k === kill[1]) s = 900_000;
      else s = this.history[m.from * 90 + m.to]!;
      return { m, s };
    });
    scored.sort((a, b) => b.s - a.s);
    return scored.map((x) => x.m);
  }

  private quiesce(pos: Position, alpha: number, beta: number, ply: number, hash: Hash): number {
    this.nodes++;
    // Quiescence used to check neither of these, so a runaway line here was
    // answerable to nothing at all -- not the depth limit, which it does not
    // have, and not the move clock, which only negamax consulted.
    if (this.timeUp()) return alpha;
    const mover = pos.sideToMove;
    const sign = mover === Color.Red ? 1 : -1;
    if (ply >= MAX_PLY) return evaluate(pos) * sign;
    const checked = isSquareAttacked(pos, this.kingSq[mover], mover === Color.Red ? Color.Black : Color.Red);

    if (!checked) {
      const standPat = evaluate(pos) * sign;
      if (standPat >= beta) return beta;
      if (standPat > alpha) alpha = standPat;
    }

    const pseudo = pseudoLegalMoves(pos, mover);
    if (!checked) pseudo.sort((a, b) => this.captureScore(pos, b) - this.captureScore(pos, a));

    let legalCount = 0;
    for (const m of pseudo) {
      if (!checked && !pos.board[m.to]) continue; // quiet moves only searched when evading check
      const piece = pos.board[m.from]!;
      const captured = pos.board[m.to] ?? null;
      const isKing = piece.type === PieceType.King;
      const savedK = isKing ? this.kingSq[mover] : -1;
      if (isKing) this.kingSq[mover] = m.to;
      const undo = pos.makeMoveFast(m);
      if (!this.legal(pos, mover)) {
        pos.unmakeMove(undo);
        if (isKing) this.kingSq[mover] = savedK;
        continue;
      }
      legalCount++;
      updateHash(hash, m, piece.color, piece.type, captured);
      const score = -this.quiesce(pos, -beta, -alpha, ply + 1, hash);
      updateHash(hash, m, piece.color, piece.type, captured);
      pos.unmakeMove(undo);
      if (isKing) this.kingSq[mover] = savedK;
      if (this.stopped) return alpha;
      if (score >= beta) return beta;
      if (score > alpha) alpha = score;
    }
    if (checked && legalCount === 0) return -(MATE - ply);
    return alpha;
  }

  private negamax(pos: Position, depth: number, alpha: number, beta: number, ply: number, hash: Hash): number {
    this.nodes++;
    if (this.timeUp()) return 0;
    if (ply > 0) {
      const prev = this.pathH1.lastIndexOf(hash.h1);
      if (prev >= 0) return this.repetitionScore(pos, prev, ply);
    }
    if (pos.halfmoveClock >= 120) return 0;
    if (depth <= 0 || ply >= MAX_PLY) return this.quiesce(pos, alpha, beta, ply, hash);

    const alphaOrig = alpha;
    const entry = this.tt.get(hash.h1);
    let ttMove = -1;
    if (entry && entry.h2 === hash.h2) {
      ttMove = entry.move;
      if (entry.depth >= depth) {
        if (entry.flag === 0) return entry.score;
        if (entry.flag === 1 && entry.score > alpha) alpha = entry.score;
        else if (entry.flag === 2 && entry.score < beta) beta = entry.score;
        if (alpha >= beta) return entry.score;
      }
    }

    const mover = pos.sideToMove;
    const ordered = this.orderMoves(pos, pseudoLegalMoves(pos, mover), ttMove, ply);
    let best = -INF;
    let bestMoveKey = -1;
    let legalCount = 0;
    const perpetualsBefore = this.perpetuals;
    this.pathH1.push(hash.h1);
    this.pathChecked.push(isSquareAttacked(pos, this.kingSq[mover], mover === Color.Red ? Color.Black : Color.Red));

    for (const m of ordered) {
      const piece = pos.board[m.from]!;
      const captured = pos.board[m.to] ?? null;
      const isKing = piece.type === PieceType.King;
      const savedK = isKing ? this.kingSq[mover] : -1;
      if (isKing) this.kingSq[mover] = m.to;
      const undo = pos.makeMoveFast(m);
      if (!this.legal(pos, mover)) {
        pos.unmakeMove(undo);
        if (isKing) this.kingSq[mover] = savedK;
        continue;
      }
      legalCount++;
      updateHash(hash, m, piece.color, piece.type, captured);
      const score = -this.negamax(pos, depth - 1, -beta, -alpha, ply + 1, hash);
      updateHash(hash, m, piece.color, piece.type, captured);
      pos.unmakeMove(undo);
      if (isKing) this.kingSq[mover] = savedK;

      if (this.stopped) {
        this.pathH1.pop();
        this.pathChecked.pop();
        return best > -INF ? best : alpha;
      }
      if (score > best) {
        best = score;
        bestMoveKey = mkey(m);
      }
      if (best > alpha) alpha = best;
      if (alpha >= beta) {
        if (!captured) {
          const kl = (this.killers[ply] ??= [-1, -1]);
          if (kl[0] !== bestMoveKey) {
            kl[1] = kl[0]!;
            kl[0] = bestMoveKey;
          }
          this.history[m.from * 90 + m.to]! += depth * depth;
        }
        break;
      }
    }
    this.pathH1.pop();
    this.pathChecked.pop();

    if (legalCount === 0) return -(MATE - ply); // no legal move = loss
    const flag = best <= alphaOrig ? 2 : best >= beta ? 1 : 0;
    // A perpetual verdict belongs to the path that produced it, not to the
    // position: reached another way, the same position is not a repetition at
    // all. Storing it would hand a win or a loss to lines that have neither.
    if (this.perpetuals === perpetualsBefore) {
      this.tt.set(hash.h1, { h2: hash.h2, depth, score: best, flag, move: bestMoveKey });
    }
    return best;
  }

  search(pos: Position, level: Level, seedHistory: number[] = [], budgetMs?: number, seedChecks: boolean[] = []): SearchResult {
    const params = LEVELS[level];
    this.nodes = 0;
    this.stopped = false;
    // A caller may give the search less time than the level's default — a bot
    // pacing itself to a human-looking move time needs the answer early so the
    // rest of the pause is its own, not the engine's.
    this.deadline = Date.now() + (budgetMs && budgetMs > 0 ? budgetMs : params.moveTimeMs);
    this.killers = [];
    this.history = new Int32Array(90 * 90);
    this.pathH1 = seedHistory.slice();
    // Without check flags a caller still gets the old behaviour: every
    // repetition through the game history scores as a draw.
    this.pathChecked = seedHistory.map((_, i) => seedChecks[i] === true);
    this.perpetuals = 0;
    this.kingSq = [pos.kingSquare(Color.Red), pos.kingSquare(Color.Black)];

    // Root legal moves (with legality via the same cached-king path).
    const rootMoves: Move[] = [];
    for (const m of pseudoLegalMoves(pos, pos.sideToMove)) {
      const mover = pos.sideToMove;
      const isKing = pos.board[m.from]!.type === PieceType.King;
      const savedK = isKing ? this.kingSq[mover] : -1;
      if (isKing) this.kingSq[mover] = m.to;
      const undo = pos.makeMoveFast(m);
      if (this.legal(pos, mover)) rootMoves.push(m);
      pos.unmakeMove(undo);
      if (isKing) this.kingSq[mover] = savedK;
    }

    if (rootMoves.length === 0) return { move: null, score: -MATE, depth: 0, nodes: 0, mate: 0 };
    if (rootMoves.length === 1) return { move: rootMoves[0]!, score: 0, depth: 0, nodes: 0, mate: null };
    if (Math.random() < params.blunder) {
      return { move: rootMoves[Math.floor(Math.random() * rootMoves.length)]!, score: 0, depth: 0, nodes: 0, mate: null };
    }

    const rootHash = fullHash(pos);
    // The root is part of the path too. It used to be left off, so a line that
    // came straight back to the position on the board went unnoticed.
    const rootMover = pos.sideToMove;
    this.pathH1.push(rootHash.h1);
    this.pathChecked.push(isSquareAttacked(pos, this.kingSq[rootMover], rootMover === Color.Red ? Color.Black : Color.Red));
    const fullWindow = params.randomness > 0;
    let bestMove = rootMoves[0]!;
    let bestScore = -INF;
    let completedDepth = 0;
    let rootScores = new Map<number, number>();

    for (let depth = 1; depth <= params.maxDepth; depth++) {
      rootMoves.sort((a, b) => (rootScores.get(mkey(b)) ?? -INF) - (rootScores.get(mkey(a)) ?? -INF));
      let localBest = -INF;
      let localBestMove = rootMoves[0]!;
      let alpha = -INF;
      const iterScores = new Map<number, number>();
      let aborted = false;

      for (const m of rootMoves) {
        const mover = pos.sideToMove;
        const piece = pos.board[m.from]!;
        const captured = pos.board[m.to] ?? null;
        const isKing = piece.type === PieceType.King;
        const savedK = isKing ? this.kingSq[mover] : -1;
        if (isKing) this.kingSq[mover] = m.to;
        const hash: Hash = { h1: rootHash.h1, h2: rootHash.h2 };
        const undo = pos.makeMoveFast(m);
        updateHash(hash, m, piece.color, piece.type, captured);
        const a = fullWindow ? -INF : alpha;
        const score = -this.negamax(pos, depth - 1, -INF, -a, 1, hash);
        pos.unmakeMove(undo);
        if (isKing) this.kingSq[mover] = savedK;
        if (this.stopped) {
          aborted = true;
          break;
        }
        iterScores.set(mkey(m), score);
        if (score > localBest) {
          localBest = score;
          localBestMove = m;
          if (score > alpha) alpha = score;
        }
      }

      if (!aborted) {
        bestMove = localBestMove;
        bestScore = localBest;
        rootScores = iterScores;
        completedDepth = depth;
        if (Math.abs(bestScore) > MATE - 1000) break;
      } else {
        break;
      }
    }

    if (params.randomness > 0 && rootScores.size > 0) {
      const candidates = rootMoves.filter((m) => (rootScores.get(mkey(m)) ?? -INF) >= bestScore - params.randomness);
      if (candidates.length > 0) {
        bestMove = candidates[Math.floor(Math.random() * candidates.length)]!;
        bestScore = rootScores.get(mkey(bestMove)) ?? bestScore;
      }
    }

    const mate = Math.abs(bestScore) > MATE - 1000 ? Math.ceil((MATE - Math.abs(bestScore)) / 2) : null;
    return { move: bestMove, score: bestScore, depth: completedDepth, nodes: this.nodes, mate };
  }
}

export function chooseMove(
  pos: Position,
  level: Level,
  seedHistory: number[] = [],
  /** optional cap on the search's thinking time, ms (defaults to the level's) */
  budgetMs?: number,
  /** parallel to seedHistory: was the side to move in check in that position? */
  seedChecks: boolean[] = [],
): SearchResult {
  return new Searcher().search(pos, level, seedHistory, budgetMs, seedChecks);
}

export function positionHash(pos: Position): number {
  return fullHash(pos).h1;
}
